function formatAddress(address) {
  if (!address) return null;
  if (typeof address === 'string') return address.trim() || null;
  if (typeof address === 'object') {
    const parts = [
      address.line1,
      address.line2,
      [address.postCode, address.city].filter(Boolean).join(' '),
      address.state,
      address.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  }
  return null;
}

function normalizeBankAccount(bankAccount, fallbackCurrency = 'EUR', provider = null) {
  if (!bankAccount || typeof bankAccount !== 'object') return null;

  const currency = String(bankAccount.currency || fallbackCurrency || 'EUR').toUpperCase();
  const rawType = bankAccount.type || (bankAccount.iban ? 'Sepa' : 'Bank');
  const type = String(rawType);

  const holderName =
    bankAccount.accountHolderName ||
    bankAccount.beneficiaryName ||
    bankAccount.name ||
    '';

  return {
    ...bankAccount,
    type,
    currency,
    accountHolderName: holderName,
    beneficiaryName: bankAccount.beneficiaryName || holderName,
    name: holderName,
    iban: bankAccount.iban || '',
    onRampProvider: provider || bankAccount.onRampProvider || null,
  };
}

export function normalizeOnRampAccountsPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return { onRampAccounts: {}, primaryAccount: null };
  }
  
  if (typeof payload.iban === 'string' || (typeof payload.name === 'string' && !payload.EUR && !payload.eur)) {
    const legacyNormalized = normalizeBankAccount(payload, 'EUR', payload.onRampProvider || 'Quantoz');
    const sepaItem = {
      onRampProvider: legacyNormalized?.onRampProvider || 'Quantoz',
      bankAccount: legacyNormalized,
    };
    return {
      onRampAccounts: { EUR: [sepaItem] },
      primaryAccount: legacyNormalized,
    };
  }

  const onRampAccounts = {};
  Object.keys(payload).forEach((rawKey) => {
    const currency = String(rawKey).toUpperCase();
    const accounts = payload[rawKey];
    if (!Array.isArray(accounts)) return;

    const normalizedList = accounts
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        const provider = item.onRampProvider || item.OnRampProvider || null;
        const rawBank = item.bankAccount || item.BankAccount || item;
        const normalizedBank = normalizeBankAccount(rawBank, currency, provider);
        if (!normalizedBank) return null;
        return {
          onRampProvider: provider,
          bankAccount: normalizedBank,
        };
      })
      .filter(Boolean);

    if (normalizedList.length > 0) {
      onRampAccounts[currency] = normalizedList;
    }
  });

  const currencies = Object.keys(onRampAccounts);
  let primaryAccount = null;

  if (onRampAccounts.EUR && onRampAccounts.EUR.length > 0) {
    primaryAccount = onRampAccounts.EUR[0].bankAccount;
  } else if (currencies.length > 0) {
    primaryAccount = onRampAccounts[currencies[0]][0].bankAccount;
  }

  return { onRampAccounts, primaryAccount };
}

const PREFERRED_RAIL_ORDER = ['Ach', 'Sepa', 'FedNow', 'Fedwire', 'Wire', 'Swift', 'SortCode'];

export function getRailShortLabel(type) {
  const normalized = String(type || '').toLowerCase();
  if (normalized === 'sepa') return 'SEPA';
  if (normalized === 'ach') return 'ACH';
  if (normalized === 'wire') return 'Wire';
  if (normalized === 'swift') return 'SWIFT';
  if (normalized === 'sortcode') return 'Sort code';
  if (normalized === 'fednow') return 'FedNow';
  if (normalized === 'fedwire') return 'Fedwire';
  return type ? String(type) : 'Bank';
}

export function getOnRampRailsForCurrency(onRampAccounts, currency = 'EUR') {
  if (!onRampAccounts || typeof onRampAccounts !== 'object') return [];
  const upper = String(currency || 'EUR').toUpperCase();
  const list = onRampAccounts[upper];
  if (!Array.isArray(list) || list.length === 0) return [];

  return list
    .map((item, index) => {
      const bankAccount = item?.bankAccount || null;
      if (!bankAccount) return null;
      const type = String(bankAccount.type || 'Bank');
      return {
        key: bankAccount.id || `${upper}-${type}-${index}`,
        type,
        shortLabel: getRailShortLabel(type),
        onRampProvider: item.onRampProvider || bankAccount.onRampProvider || null,
        bankAccount,
      };
    })
    .filter(Boolean);
}

export function getPreferredRailType(rails) {
  if (!Array.isArray(rails) || rails.length === 0) return null;
  const byPreferred = PREFERRED_RAIL_ORDER.find((preferred) =>
    rails.some((rail) => String(rail.type).toLowerCase() === preferred.toLowerCase()),
  );
  if (byPreferred) {
    const match = rails.find((rail) => String(rail.type).toLowerCase() === byPreferred.toLowerCase());
    return match?.type || rails[0].type;
  }
  return rails[0].type;
}

export function getOnRampAccountByCurrency(onRampAccounts, currency = 'EUR', railType = null) {
  const rails = getOnRampRailsForCurrency(onRampAccounts, currency);
  if (rails.length === 0) return null;

  if (railType) {
    const match = rails.find((rail) => String(rail.type).toLowerCase() === String(railType).toLowerCase());
    if (match) return match.bankAccount;
  }

  const preferredType = getPreferredRailType(rails);
  const preferred = rails.find((rail) => String(rail.type).toLowerCase() === String(preferredType).toLowerCase());
  return (preferred || rails[0]).bankAccount;
}

export function getAvailableOnRampCurrencies(onRampAccounts, configCurrencies) {
  const set = new Set();
  if (Array.isArray(configCurrencies)) {
    configCurrencies.forEach((c) => {
      if (c) set.add(String(c).toUpperCase());
    });
  }
  if (onRampAccounts && typeof onRampAccounts === 'object') {
    Object.keys(onRampAccounts).forEach((c) => {
      if (c && Array.isArray(onRampAccounts[c]) && onRampAccounts[c].length > 0) {
        set.add(String(c).toUpperCase());
      }
    });
  }
  return Array.from(set);
}

export function getOnRampProvidersFromAccounts(onRampAccounts) {
  const providers = new Set();
  if (!onRampAccounts || typeof onRampAccounts !== 'object') return [];

  Object.values(onRampAccounts).forEach((list) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      const provider =
        item?.onRampProvider ||
        item?.OnRampProvider ||
        item?.bankAccount?.onRampProvider ||
        item?.bankAccount?.OnRampProvider ||
        null;
      if (provider) providers.add(String(provider));
    });
  });

  return Array.from(providers);
}

export function getPaymentMethodLabel(bankAccount, currency = 'EUR') {
  const type = String(bankAccount?.type || '').toLowerCase();
  const curr = String(currency || bankAccount?.currency || 'EUR').toUpperCase();

  if (type === 'sepa') return 'Bank transfer (SEPA)';
  if (type === 'ach') return 'Bank transfer (ACH)';
  if (type === 'wire') return 'Bank transfer (Wire)';
  if (type === 'swift') return 'SWIFT transfer';
  if (type === 'sortcode') return 'Bank transfer (Faster Payments / Sort code)';
  if (type === 'fednow') return 'FedNow instant transfer';
  if (type === 'fedwire') return 'Fedwire transfer';

  if (curr === 'EUR') return 'Bank transfer (SEPA)';
  if (curr === 'GBP') return 'Bank transfer (Faster Payments / Sort code)';
  if (curr === 'USD') return 'Bank transfer (ACH / Wire)';

  return 'Bank transfer';
}

export function getOnRampAccountFields(bankAccount) {
  if (!bankAccount || typeof bankAccount !== 'object') return [];

  const type = String(bankAccount.type || '').toLowerCase();
  const name = bankAccount.accountHolderName || bankAccount.beneficiaryName || bankAccount.name || '';
  const fields = [];

  const add = (label, value, isMono = false) => {
    if (value && String(value).trim()) {
      fields.push({ label, value: String(value).trim(), isMono });
    }
  };

  switch (type) {
    case 'sepa':
      add('Account name', name);
      add('IBAN', bankAccount.iban, true);
      add('BIC / SWIFT', bankAccount.bic || bankAccount.swiftCode, true);
      break;

    case 'ach':
      add('Beneficiary name', name);
      add('Routing number', bankAccount.routingNumber, true);
      add('Account number', bankAccount.accountNumber, true);
      add('Account type', bankAccount.accountType);
      break;

    case 'wire':
      add('Beneficiary name', name);
      add('Routing number', bankAccount.routingNumber, true);
      add('Account number', bankAccount.accountNumber, true);
      add('Account type', bankAccount.accountType);
      add('Beneficiary address', formatAddress(bankAccount.address));
      break;

    case 'swift':
      add('Beneficiary name', name);
      add('SWIFT / BIC', bankAccount.swiftCode || bankAccount.bic, true);
      add('Account number / IBAN', bankAccount.accountNumber || bankAccount.iban, true);
      add('Bank name', bankAccount.bankName);
      add('Bank address', formatAddress(bankAccount.bankAddress));
      add('Beneficiary address', formatAddress(bankAccount.beneficiaryAddress));
      break;

    case 'sortcode':
      add('Beneficiary name', name);
      add('Sort code', bankAccount.sortCode, true);
      add('Account number', bankAccount.accountNumber, true);
      break;

    case 'fednow':
    case 'fedwire':
      add('Beneficiary name', name);
      add('Routing number', bankAccount.routingNumber, true);
      add('Account number', bankAccount.accountNumber, true);
      add('Account type', bankAccount.accountType);
      break;

    default:
      add('Account name', name);
      if (bankAccount.iban) add('IBAN', bankAccount.iban, true);
      if (bankAccount.accountNumber) add('Account number', bankAccount.accountNumber, true);
      if (bankAccount.routingNumber) add('Routing number', bankAccount.routingNumber, true);
      if (bankAccount.swiftCode || bankAccount.bic) add('SWIFT / BIC', bankAccount.swiftCode || bankAccount.bic, true);
      break;
  }

  return fields;
}
